/** @file jointFracs: per node, the pixel fraction its drawn part puts its pivot at (where the joint shows). */
import type { ImpostorList } from '@zilo/pose3d-render-core/contract';
import { IMPOSTOR_LAYOUT as L } from '@zilo/pose3d-render-core/layout/impostorLayout';

/** The fractional part of `v` in `[0, 1)`. */
const fracOf = (v: number): number => v - Math.floor(v);

/** The row at `o` sets node `i`'s fractions unless the node is out of range or an earlier row set them. */
function take(list: ImpostorList, o: number, i: number, n: number, out: Float64Array): void {
  if (i < 0 || i >= n || !Number.isNaN(out[2 * i] ?? 0)) return;
  out[2 * i] = fracOf(list[o + L.pivotX] ?? 0);
  out[2 * i + 1] = fracOf(list[o + L.pivotY] ?? 0);
}

/**
 * Writes `out[2i], out[2i + 1]` for nodes `0..n-1`: the fractional part of the display pivot (`pivotX`, `pivotY`
 * slots: half pixels and `halfPixel` already applied, flips mirrored) of the first placed impostor on node `i`
 * (`nodeOf` maps the row's `drawable` slot to a node index, -1 none), else `fallback` on both axes (0.5 for a
 * half-pixel profile: the centre of the pixel a point falls in). Allocates nothing; returns `out`.
 */
export function jointFracs(list: ImpostorList, count: number, nodeOf: (drawable: number) => number, n: number, fallback: number, out: Float64Array): Float64Array {
  out.fill(NaN, 0, 2 * n);
  for (let k = 0; k < count; k++) take(list, k * L.stride, nodeOf(list[k * L.stride + L.drawable] ?? -1), n, out);
  for (let j = 0; j < 2 * n; j++) if (Number.isNaN(out[j] ?? 0)) out[j] = fallback;
  return out;
}
